<script setup lang="ts">
import { onMounted, type Ref } from 'vue';
import Column from 'primevue/column';
import { useRoute, useRouter } from 'vue-router';
import { getI18n } from '@/helpers/i18n';
import { useToast } from 'primevue/usetoast';
import { errToToast } from '@/helpers/tools';
import { useBrowserSessionValue } from '@/helpers/browser_session';
import { useCollection, type SortOption } from '@/composables/managedCollection';
import KpBaseCollectionGrid from '@/components/KpBaseCollectionGrid.vue';
import {
    AnteilWaermeerzeugerHeizung,
    AnteilWaermeerzeugerHeizungCollection,
} from '@/models/AnteilWaermeerzeugerHeizung';

const router = useRouter();
const route = useRoute();
const toast = useToast();

const tableSort: Ref<SortOption[]> = useBrowserSessionValue(`eco2stat-${route.fullPath}-tableSort`, [
    { direction: 'asc', property: 'kombination' },
    { direction: 'desc', property: 'anteil_waermeerzeuger_heizung_1' },
]);
const pageSize: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-pageSize`, 100);
const page: Ref<number> = useBrowserSessionValue(`eco2stat-${route.fullPath}-page`, 1);
const {
    collection,
    serverTotal,
    reload: reloadCollection,
} = useCollection(new AnteilWaermeerzeugerHeizungCollection(), {
    autoload: false,
    tableSort,
    pageSize,
    page,
});

onMounted(() => {
    if (collection.loading.value !== true) {
        collection.query();
    }
});

function editRecord(r: AnteilWaermeerzeugerHeizung) {
    router.push({ name: 'bg_geb_anteil_waermeerzeuger_heizung_edit', params: { id: r?.id } });
}

async function deleteRecord(r: AnteilWaermeerzeugerHeizung) {
    if (window.confirm(getI18n().t('sicher_loeschen'))) {
        try {
            await r.destroy();
            await reloadCollection();
        } catch (e) {
            errToToast(e, toast);
        }
    }
}

function newRecord() {
    router.push({ name: 'bg_geb_anteil_waermeerzeuger_heizung_edit', params: { id: 'new' } });
}
</script>
<template>
    <div style="height: 100%">
        <KpBaseCollectionGrid
            :collection="collection"
            v-model:table-sort="tableSort"
            :title="$t('anteile_waermeerzeuger_heizung')"
            scrollable
            scroll-height="flex"
            paginator
            v-model:page="page"
            v-model:pageSize="pageSize"
            :total-records="serverTotal"
            @editrecord="editRecord($event as AnteilWaermeerzeugerHeizung)"
            @deleterecord="deleteRecord($event as AnteilWaermeerzeugerHeizung)"
            @new="newRecord"
        >
            <Column field="id" sortable :header="$t('id')" style="width: 5rem"></Column>
            <Column field="kombination" sortable :header="$t('kombination')" style="width: 5rem"></Column>
            <Column field="nutzungsart" sortable :header="$t('nutzungsart')"></Column>
            <Column field="waermeerzeuger_heizung_1" sortable :header="$t('waermeerzeuger_heizung_1')"></Column>
            <Column field="anteil_waermeerzeuger_heizung_1" sortable :header="$t('anteil_waermeerzeuger_heizung_1_kurz')"></Column>
            <Column field="waermeerzeuger_heizung_2" sortable :header="$t('waermeerzeuger_heizung_2')"></Column>
            <Column field="anteil_waermeerzeuger_heizung_2" sortable :header="$t('anteil_waermeerzeuger_heizung_2_kurz')"></Column>
        </KpBaseCollectionGrid>
    </div>
</template>
